/**
 * «Мои задачи» — сводный список по всем проектам, куда человек допущен.
 *
 * Данные v2 (DEV-2045, DEV-2046): чипы считает сервер одним запросом, секции
 * по проектам приходят из ListIssueGroups с честными счётчиками, список идёт
 * страницами. Прежняя реализация выкачивала ВСЕ страницы по 100 и делала это
 * дважды (assigned + watched) на каждый заход — только чтобы посчитать чипы.
 *
 * Статус задачи — колонка её проекта, и колонки догружаются только для тех
 * проектов, которые в списке действительно есть.
 */
import { issueApi } from '@/api'
import { toRow, type IssueRow } from '@/features/issues/api'
import { listColumns, type ProjectColumn } from '@/features/projects/api'

import type {
  IssueServiceListMyIssuesFilterStatusCategoriesEnum,
  V1Issue,
} from '@/api/clients/tracker-issue'

/** Отбор экрана — он же три чипа макета */
export type MyFilter = 'assigned' | 'watching' | 'overdue'

/** Сколько строк приносит одна страница списка */
export const PAGE = 100

/** Сколько строк несёт секция проекта при загрузке */
export const MY_PER_GROUP = 10

/** Задача сводного списка: строка плюс проект, по которому её группируют */
export interface MyIssue extends IssueRow {
  projectId: string
  /** Префикс ключа — он же ключ проекта: `SALES-214` → `SALES` */
  projectKey: string
}

const withProject = (issue: V1Issue): MyIssue => ({
  ...toRow(issue),
  projectId: issue.projectId ?? '',
  projectKey: (issue.key ?? '').split('-')[0] ?? '',
})

/** По чему список умеет сортировать — ровно то, что принимает сервер */
export type MySortField = 'due_date' | 'priority' | 'updated_at' | 'key'

/**
 * Отбор и порядок уходят на сервер, а не считаются здесь: список приезжает
 * страницами, и клиентский отбор врал бы на всём, что осталось за первой.
 */
export interface MyQuery {
  projectIds?: string[]
  statusCategories?: string[]
  /** Срок не позже этого момента — им спрашивают «что горит на неделе» */
  dueBefore?: string
  /** Текст по названию и описанию — поле поиска экрана (DEV-2046) */
  search?: string
  sortBy?: MySortField
  sortDesc?: boolean
}

/** Начало сегодняшнего дня в поясе человека: границу суток серверу не угадать */
export const dayStart = (now = new Date()): Date =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate())

/** Сужение по чипу: «просрочено» — это срок раньше начала сегодняшнего дня */
const chipDueBefore = (filter: MyFilter, query: MyQuery): string | undefined =>
  filter === 'overdue' ? dayStart().toISOString() : query.dueBefore

/** Счётчики чипов — с сервера, по текущему отбору (DEV-2035) */
export interface MyCounts {
  assigned: number
  watchedOnly: number
  overdue: number
}

export const myCounts = async (query: MyQuery = {}): Promise<MyCounts> => {
  const { data } = await issueApi.issueServiceGetMyIssueCounts(
    undefined, // filterAssigneeIds — счётчики всегда про запросившего
    undefined, // filterLabelIds
    undefined, // filterTypes
    undefined, // filterPriorities
    undefined, // filterColumnIds
    query.search?.trim() || undefined,
    undefined, // filterParentId
    undefined, // filterOnlyOpen — открытость сервер держит сам
    query.dueBefore,
    query.projectIds?.length ? query.projectIds : undefined,
    (query.statusCategories?.length ? query.statusCategories : undefined) as
      | IssueServiceListMyIssuesFilterStatusCategoriesEnum[]
      | undefined,
    undefined, // filterDueAfter
    undefined, // filterAncestorId
    undefined, // filterReporterIds
    undefined, // filterUnassigned
    undefined, // filterNoDueDate
    undefined, // filterNoColumn
    dayStart().toISOString(),
  )

  // Счётчики приходят строками: в protobuf это uint64, в number он не влезает.
  return {
    assigned: Number(data.assigned ?? 0),
    watchedOnly: Number(data.watchedOnly ?? 0),
    overdue: Number(data.overdue ?? 0),
  }
}

export interface MyPage {
  issues: MyIssue[]
  total: number
}

/**
 * Одна страница списка — БЕЗ дочитывания всех страниц (DEV-2045): чипы теперь
 * серверные, и выкачивать сотни задач ради счётчиков больше незачем.
 *
 * «Наблюдаю» отдаёт назначенные вместе с наблюдаемыми: отдельного режима
 * «только наблюдаемые» у сервиса нет, а вычитание одной выдачи из другой — это
 * и была двойная выкачка. Число «только наблюдаю» при этом честное — с
 * сервера.
 */
export const listMine = async (
  filter: MyFilter,
  query: MyQuery = {},
  page = 1,
  perPage = PAGE,
): Promise<MyPage> => {
  // Клиент сгенерирован с позиционными аргументами, и порядок здесь —
  // единственное, что связывает поле фильтра с его местом в запросе. Сдвиг на
  // одну позицию превратил бы «срок не позже» в «срок не раньше» молча,
  // поэтому URL закреплён тестом.
  const { data } = await issueApi.issueServiceListMyIssues(
    filter === 'watching',
    true, // onlyOpen: закрытым место в списке проекта, а не в «моих»
    String(page),
    String(perPage),
    undefined, // filterAssigneeIds — исполнитель здесь всегда сам запросивший
    undefined, // filterLabelIds
    undefined, // filterTypes
    undefined, // filterPriorities
    undefined, // filterColumnIds
    query.search?.trim() || undefined,
    undefined, // filterParentId
    undefined, // filterOnlyOpen
    chipDueBefore(filter, query),
    query.projectIds?.length ? query.projectIds : undefined,
    (query.statusCategories?.length ? query.statusCategories : undefined) as
      | IssueServiceListMyIssuesFilterStatusCategoriesEnum[]
      | undefined,
    undefined, // filterDueAfter
    undefined, // filterAncestorId
    undefined, // filterReporterIds — исполнитель здесь сам запросивший, автор не спрашивается
    undefined, // filterUnassigned
    undefined, // filterNoDueDate
    undefined, // filterNoColumn
    query.sortBy,
    query.sortDesc,
  )

  return {
    issues: (data.issues ?? []).map(withProject),
    total: Number(data.pageInfo?.total ?? 0),
  }
}

/** Секция сводного списка: проект, честный счётчик и первые строки */
export interface MyGroupSection {
  /** Идентификатор проекта — им же догружается остаток группы */
  key: string
  /** Ключ проекта из строк; подпись группе даёт экран по своему списку проектов */
  projectKey: string
  total: number
  issues: MyIssue[]
}

/**
 * Секции по проектам — сервером (DEV-2046): группы считаются по всей выборке,
 * клиентская группировка одной страницы рвала группы и врала в счётчиках.
 */
export const listMyGroups = async (
  filter: MyFilter,
  query: MyQuery = {},
): Promise<MyGroupSection[]> => {
  const { data } = await issueApi.issueServiceListIssueGroups(
    undefined, // projectId — сквозной режим
    undefined, // filterAssigneeIds
    undefined, // filterLabelIds
    undefined, // filterTypes
    undefined, // filterPriorities
    undefined, // filterColumnIds
    query.search?.trim() || undefined,
    undefined, // filterParentId
    // Открытость просит клиент: в режиме `my_issues` сервер её не сужает сам,
    // и без этого флага в секции приезжали закрытые — при том, что чипы
    // считают строго открытые, а «Показать ещё» их и не спрашивает (DEV-2076).
    true, // filterOnlyOpen
    chipDueBefore(filter, query),
    query.projectIds?.length ? query.projectIds : undefined,
    (query.statusCategories?.length ? query.statusCategories : undefined) as never,
    undefined, // filterDueAfter
    undefined, // filterAncestorId
    undefined, // filterReporterIds
    undefined, // filterUnassigned
    undefined, // filterNoDueDate
    undefined, // filterNoColumn
    'ISSUE_GROUP_BY_PROJECT',
    query.sortBy ?? 'due_date',
    query.sortDesc,
    MY_PER_GROUP,
    true, // myIssues
    filter === 'watching',
    undefined, // dayStart — корзины срока здесь не ось
  )

  return (data.groups ?? []).map((group) => {
    const issues = (group.issues ?? []).map(withProject)

    return {
      key: group.key ?? '',
      projectKey: issues[0]?.projectKey ?? group.title ?? '',
      total: Number(group.total ?? 0),
      issues,
    }
  })
}

/** Просрочено — срок раньше сегодняшнего дня; сегодняшний срок ещё не долг. */
export const isOverdue = (issue: MyIssue, now = new Date()): boolean => {
  if (!issue.dueDate) return false

  const due = new Date(issue.dueDate)

  if (Number.isNaN(due.getTime())) return false

  return due.getTime() < dayStart(now).getTime()
}

/** Колонки проектов, встретившихся в списке — по ним рисуются пилюли статусов. */
export const loadColumns = async (
  projectIds: string[],
): Promise<Record<string, ProjectColumn[]>> => {
  const unique = [...new Set(projectIds.filter(Boolean))]
  const loaded = await Promise.all(unique.map(async (id) => [id, await listColumns(id)] as const))

  return Object.fromEntries(loaded)
}
